import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AppShell from "@/components/AppShell";
import { AssetGlyph } from "@/components/market/PriceCell";
import { P2PHeader, P2PPage } from "@/components/p2p/P2PSubNav";
import { api } from "@/lib/api";

const METHODS = [
  { value: "UPI", label: "UPI" },
  { value: "IMPS", label: "IMPS" },
  { value: "BANK", label: "Bank transfer" },
  { value: "PHONEPE", label: "PhonePe" },
  { value: "PAYTM", label: "Paytm" },
  { value: "GPAY", label: "GPay" },
];
const METHOD_LABEL = Object.fromEntries(METHODS.map((m) => [m.value, m.label]));
const COINS = ["USDT"];
const FIATS = ["INR"];
const STEPS = ["Order placed", "Payment sent", "Payment confirmed", "Crypto released"];

const fmtNum = (v, max = 2) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: max });
const errText = (e) => e?.response?.data?.detail || e?.message || "Something went wrong";

function TradePanel({ adId, side, onClose }) {
  const nav = useNavigate();
  const [ad, setAd] = useState(null);
  const [myPms, setMyPms] = useState([]);
  const [pmId, setPmId] = useState("");
  const [amount, setAmount] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    api
      .get(`/api/p2p/ads/${adId}`)
      .then(async ({ data }) => {
        if (!alive) return;
        setAd(data);
        setAmount(String(Math.ceil(Number(data.min_order_inr) || 0) || ""));
        if (data.side === "sell") {
          if (data.payment_methods?.length) setPmId(data.payment_methods[0].pm_id);
          return;
        }
        const mine = await api.get("/api/p2p/payment-methods");
        const accepted = new Set((data.payment_methods || []).map((p) => p.type));
        const usable = (mine.data.payment_methods || []).filter((p) => !accepted.size || accepted.has(p.type));
        if (!alive) return;
        setMyPms(usable);
        if (usable.length) setPmId(usable[0].pm_id);
      })
      .catch((e) => alive && setError(errText(e)));
    return () => {
      alive = false;
    };
  }, [adId]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const takerBuys = ad ? ad.side === "sell" : side === "buy";
  const merchant = ad?.maker?.nickname || "merchant";
  const crypto = ad && Number(ad.price) > 0 ? Number(amount || 0) / Number(ad.price) : 0;
  const pmOptions = takerBuys ? ad?.payment_methods || [] : myPms;

  async function openOrder() {
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/api/p2p/orders", { ad_id: adId, fiat_amount: amount, payment_method_id: pmId });
      nav(`/p2p/orders/${data.order_id}`);
    } catch (e) {
      setError(errText(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="p2p-trade-panel">
      <button type="button" aria-label="Close" className="absolute inset-0 bg-background/70 backdrop-blur-sm" onClick={onClose} />
      <aside className="page-fade relative h-full w-full max-w-2xl overflow-y-auto border-l border-border bg-surface p-6">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="flex items-center gap-2 text-xl font-semibold">
              <AssetGlyph symbol={ad?.asset || "USDT"} size={28} />
              {takerBuys ? "Buy" : "Sell"} {ad?.asset || "USDT"} {takerBuys ? "from" : "to"} {merchant}
            </h2>
            {ad ? (
              <p className="num mt-1 text-sm text-muted-foreground">
                {ad.fiat} {fmtNum(ad.price)} per {ad.asset} · window {ad.payment_window_min}:00
              </p>
            ) : (
              <div className="skeleton mt-2 h-4 w-48" />
            )}
          </div>
          <button type="button" onClick={onClose} className="rounded-lg border border-border px-2.5 py-1 text-sm text-muted-foreground">
            Close
          </button>
        </div>

        {ad && (
          <>
            <div className="mt-5 rounded-xl border border-border bg-background/40 p-4">
              <label className="text-xs text-muted-foreground" htmlFor="p2p-amount">
                You {takerBuys ? "pay" : "receive"} ({ad.fiat})
              </label>
              <input
                id="p2p-amount"
                value={amount}
                inputMode="decimal"
                data-testid="order-fiat-amount"
                onChange={(e) => setAmount(e.target.value.replace(/[^\d.]/g, ""))}
                className="num mt-1.5 w-full rounded-lg border border-border bg-surface-2 px-3 py-2.5 text-lg outline-none focus:border-primary"
              />
              <div className="num mt-3 flex justify-between text-sm">
                <span className="text-muted-foreground">You {takerBuys ? "receive" : "send"}</span>
                <span>
                  {crypto.toFixed(6)} {ad.asset}
                </span>
              </div>
              <div className="num mt-1 flex justify-between text-xs text-muted-foreground">
                <span>Limits</span>
                <span>
                  {ad.fiat} {fmtNum(ad.min_order_inr)} – {fmtNum(ad.max_order_inr)}
                </span>
              </div>
              <div className="num mt-1 flex justify-between text-xs text-muted-foreground">
                <span>Available</span>
                <span>
                  {Number(ad.available_amount).toFixed(3)} {ad.asset}
                </span>
              </div>
            </div>

            <div className="mt-4">
              <label className="text-xs text-muted-foreground" htmlFor="p2p-pm">
                {takerBuys ? "Pay with" : "Get paid to"}
              </label>
              <select
                id="p2p-pm"
                value={pmId}
                data-testid="order-pm"
                onChange={(e) => setPmId(e.target.value)}
                disabled={!pmOptions.length}
                className="mt-1.5 w-full rounded-lg border border-border bg-surface-2 px-3 py-2.5 text-sm outline-none focus:border-primary disabled:opacity-50"
              >
                {pmOptions.map((p) => (
                  <option key={p.pm_id} value={p.pm_id}>
                    {p.display_name} · {METHOD_LABEL[p.type] || p.type}
                  </option>
                ))}
              </select>
              {!takerBuys && !myPms.length && (
                <p className="mt-2 rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-xs text-warning">
                  You have no saved method this buyer accepts (
                  {(ad.payment_methods || []).map((p) => METHOD_LABEL[p.type] || p.type).join(", ")}).{" "}
                  <Link to="/p2p/payment-methods" className="underline">
                    Add one
                  </Link>{" "}
                  to sell on this offer.
                </p>
              )}
            </div>

            {ad.terms ? (
              <div className="mt-4 rounded-xl border border-border bg-background/40 p-4">
                <p className="text-xs text-muted-foreground">Merchant terms</p>
                <p className="mt-1.5 whitespace-pre-wrap text-sm">{ad.terms}</p>
              </div>
            ) : null}

            <ol className="mt-6 space-y-3">
              {STEPS.map((s, i) => (
                <li key={s} className="flex items-center gap-3">
                  <span
                    className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-xs transition ${
                      i === 0 ? "brand-gradient text-primary-foreground" : "border border-border text-muted-foreground"
                    }`}
                  >
                    {i + 1}
                  </span>
                  <span className={`text-sm ${i === 0 ? "" : "text-muted-foreground"}`}>{s}</span>
                </li>
              ))}
            </ol>
          </>
        )}

        {error && (
          <p className="mt-5 rounded-lg border border-down/40 bg-down/10 px-3 py-2 text-sm text-down" data-testid="order-error">
            {error}
          </p>
        )}

        <button
          type="button"
          onClick={openOrder}
          disabled={!ad || busy || !amount || !pmId}
          data-testid="order-submit"
          className="brand-gradient mt-5 w-full rounded-lg py-2.5 text-sm font-semibold text-primary-foreground disabled:opacity-40"
        >
          {busy ? "Opening order…" : "Place order"}
        </button>

        <div className="mt-6 rounded-xl border border-border">
          <p className="border-b border-border px-4 py-2.5 text-sm font-medium">Chat with {merchant}</p>
          <p className="p-4 text-sm text-muted-foreground">
            The trade chat opens with the order. {takerBuys ? "The seller's" : "Your"} {ad?.asset || "USDT"} stays in escrow
            until payment is confirmed.
          </p>
        </div>
      </aside>
    </div>
  );
}

export default function P2PMarketplace() {
  const { adId } = useParams();
  const nav = useNavigate();
  const [side, setSide] = useState("buy");
  const [coin, setCoin] = useState("USDT");
  const [fiat, setFiat] = useState("INR");
  const [method, setMethod] = useState(null);
  const [minAmt, setMinAmt] = useState("");
  const [ads, setAds] = useState(null);
  const [error, setError] = useState("");
  const [open, setOpen] = useState(adId || null);

  useEffect(() => setOpen(adId || null), [adId]);

  const load = useCallback(async () => {
    try {
      const params = { side, asset: coin, fiat, limit: 50 };
      if (method) params.payment_method = method;
      const { data } = await api.get("/api/p2p/ads", { params });
      setAds(data.ads || []);
      setError("");
    } catch (e) {
      setError(e?.response?.status === 401 ? "" : errText(e));
      setAds((a) => a || []);
    }
  }, [side, coin, fiat, method]);

  useEffect(() => {
    load();
    const id = setInterval(load, 8000);
    return () => clearInterval(id);
  }, [load]);

  const offers = useMemo(() => {
    let list = ads || [];
    if (minAmt) list = list.filter((o) => Number(o.max_order_inr) >= Number(minAmt));
    return list;
  }, [ads, minAmt]);

  function closePanel() {
    setOpen(null);
    if (adId) nav("/p2p", { replace: true });
  }

  return (
    <AppShell bare>
      <P2PPage>
        <P2PHeader>
          <Link
            to="/p2p/my-ads?action=create"
            data-testid="p2p-create-ad"
            className="rounded-lg border border-border bg-surface-2 px-3 py-2 text-xs transition hover:border-primary"
          >
            Post an offer
          </Link>
        </P2PHeader>

        <div className="mt-6 flex flex-wrap items-center gap-3">
          <div className="flex gap-1 rounded-lg border border-border bg-surface-2 p-1">
            {["buy", "sell"].map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => setSide(s)}
                data-testid={`p2p-tab-${s}`}
                className={`rounded-md px-4 py-1.5 text-sm font-medium capitalize transition ${
                  side === s ? (s === "buy" ? "bg-up/20 text-up" : "bg-down/20 text-down") : "text-muted-foreground"
                }`}
              >
                {s}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 rounded-lg border border-border bg-surface-2 pl-2.5 focus-within:border-primary">
            <AssetGlyph symbol={coin} size={20} />
            <select
              aria-label="Coin"
              value={coin}
              onChange={(e) => setCoin(e.target.value)}
              className="bg-transparent py-2 pr-3 text-sm outline-none"
            >
              {COINS.map((c) => (
                <option key={c}>{c}</option>
              ))}
            </select>
          </div>
          <select
            aria-label="Fiat currency"
            value={fiat}
            onChange={(e) => setFiat(e.target.value)}
            className="rounded-lg border border-border bg-surface-2 px-3 py-2 text-sm outline-none focus:border-primary"
          >
            {FIATS.map((f) => (
              <option key={f}>{f}</option>
            ))}
          </select>
          <input
            value={minAmt}
            onChange={(e) => setMinAmt(e.target.value.replace(/\D/g, ""))}
            placeholder="Min amount"
            data-testid="amount-filter"
            className="num w-32 rounded-lg border border-border bg-surface-2 px-3 py-2 text-sm outline-none focus:border-primary"
          />
          <div className="flex flex-wrap gap-1.5">
            {METHODS.map((m) => (
              <button
                key={m.value}
                type="button"
                onClick={() => setMethod(method === m.value ? null : m.value)}
                data-testid={`pm-filter-${m.value}`}
                className={`rounded-full border px-3 py-1 text-xs transition ${
                  method === m.value ? "border-primary text-foreground" : "border-border text-muted-foreground hover:text-foreground"
                }`}
              >
                {m.label}
              </button>
            ))}
          </div>
        </div>

        {error && (
          <p className="mt-4 rounded-lg border border-down/40 bg-down/10 px-3 py-2 text-sm text-down">{error}</p>
        )}

        <div className="mt-6 overflow-x-auto rounded-xl border border-border bg-surface/40">
          <div className="min-w-[820px]">
            <div className="table-head grid grid-cols-[1.5fr_1fr_1.2fr_1fr_1fr_110px] gap-3 px-4 py-2.5">
              <span>Merchant</span>
              <span className="text-right">Price</span>
              <span className="text-right">Limits</span>
              <span className="text-right">Available</span>
              <span>Payment</span>
              <span />
            </div>
            {ads === null ? (
              <div className="space-y-2 p-4">
                {[0, 1, 2].map((i) => (
                  <div key={i} className="skeleton h-10 w-full" />
                ))}
              </div>
            ) : offers.length === 0 ? (
              <p className="px-4 py-14 text-center text-sm text-muted-foreground">
                No offers match these filters. Remove the payment method or lower the amount.
              </p>
            ) : (
              offers.map((o) => (
                <div
                  key={o.ad_id}
                  data-testid={`ad-row-${o.ad_id}`}
                  className="terminal-row grid-cols-[1.5fr_1fr_1.2fr_1fr_1fr_110px] gap-3 px-4 py-3"
                >
                  <div className="flex items-center gap-3">
                    <span className="flex h-8 w-8 items-center justify-center rounded-full bg-surface-2 text-xs">
                      {(o.maker?.nickname || "Tr").slice(0, 2)}
                    </span>
                    <div>
                      <p className="flex items-center gap-1.5 text-sm font-medium">
                        {o.maker?.nickname || "Trader"}
                        {o.maker?.is_merchant && <span className="text-xs text-up">✓</span>}
                      </p>
                      <p className="num text-xs text-muted-foreground">
                        {o.maker?.trades_total ?? 0} orders · {Number(o.maker?.completion_rate_30d ?? 100).toFixed(0)}%
                      </p>
                    </div>
                  </div>
                  <div className="num text-right text-sm">
                    {o.fiat} {fmtNum(o.price)}
                  </div>
                  <div className="num text-right text-xs text-muted-foreground">
                    {fmtNum(o.min_order_inr, 0)} – {fmtNum(o.max_order_inr, 0)}
                  </div>
                  <div className="num flex items-center justify-end gap-1.5 text-right text-sm">
                    <AssetGlyph symbol={o.asset} size={18} />
                    {Number(o.available_amount).toFixed(3)} {o.asset}
                  </div>
                  <div className="flex flex-wrap gap-1">
                    {(o.payment_method_types || []).map((m) => (
                      <span key={m} className="rounded bg-surface-2 px-1.5 py-0.5 text-[10px] text-muted-foreground">
                        {METHOD_LABEL[m] || m}
                      </span>
                    ))}
                  </div>
                  <button
                    type="button"
                    onClick={() => setOpen(o.ad_id)}
                    data-testid={`open-ad-${o.ad_id}`}
                    className={`justify-self-end rounded-lg px-3 py-1.5 text-xs font-semibold ${
                      side === "buy" ? "bg-up/15 text-up hover:bg-up/25" : "bg-down/15 text-down hover:bg-down/25"
                    }`}
                  >
                    {side === "buy" ? "Buy" : "Sell"} {o.asset}
                  </button>
                </div>
              ))
            )}
          </div>
        </div>

        {open && <TradePanel key={open} adId={open} side={side} onClose={closePanel} />}
      </P2PPage>
    </AppShell>
  );
}
